import {
    Property,
    SsqSsqHeading,
    SsqSsqHeadingSsqProperty,
    SsqVersion,
} from "src/api/sure-api/ssq/useGetOneSsqVersion";
import {
    PropertyBuilder,
    PropertyValue,
    SsqPropertyWithIndexesKey,
    UserProperty,
} from "../Property/Builder/PropertyBuilder";
import { SsqProperty } from "src/api/sure-api/actions/useGetOneAction";
import { Post } from "src/api/client-api/post/postSchema";
import { MainScore, NumericalScoreValue } from "src/api/client-api/olapScores/olapScoresSchema";
import { OlapScoresService } from "../OlapScores/OlapScoresService";

export type SummaryGlobal = {
    id: string;
    // id: ProductSummaryType;
    label: string;
    score: NumericalScoreValue;
};

export type SummaryDetailItem = {
    id: number;
    label: string;
    score: NumericalScoreValue;
};

export type SummaryDetail = Partial<
    Record<
        // ProductSummaryType,
        string,
        SummaryDetailItem[]
    >
>;
export type SummaryData = {
    totalScore: number;
    summary: SummaryGlobal[];
    detail: SummaryDetail;
};

export type ScoreDetailBuilderConstructor = {
    ssqVersion: SsqVersion;
    post: Pick<Post, "campaign" | "id" | "elements">;
    olapScores?: MainScore;
};

// For PropertyBuilder
type ExtraPropertyKeys = Record<never, unknown>;
type PropertyWithIndexes = SsqPropertyWithIndexesKey<ExtraPropertyKeys>;

type PropertyMapping = {
    byProperty: Partial<Record<PropertyKey, SsqProperty>>;
    byHeadingId: Partial<Record<PropertyKey, SsqProperty[]>>;
};

export class ScoreDetailAbstract {
    public getSummary = (): SummaryData | null => {
        return null;
    };
}

export class ScoreDetailBuilder extends ScoreDetailAbstract {
    private post: Pick<Post, "campaign" | "id" | "elements">;
    public ssqVersion: SsqVersion;
    private olapScores: MainScore | undefined;

    private _allPropertyByIds: Partial<Record<PropertyKey, SsqProperty>>;

    public constructor(options: ScoreDetailBuilderConstructor) {
        super();

        this.ssqVersion = options.ssqVersion;
        this.post = options.post;
        this.olapScores = options.olapScores;

        const allPropertyByIds = this.preparePropertyIdMapping(options.ssqVersion);
        this._allPropertyByIds = allPropertyByIds["byProperty"];
    }

    private handlePropertyDimension = (ssqSsqHeading: SsqSsqHeading): UserProperty<ExtraPropertyKeys>[] | null => {
        const propertyScores = OlapScoresService.findDimension("properties", this.olapScores?.dimensions);

        const propertyIds = ssqSsqHeading.ssqSsqHeadingSsqProperties
            .map((sSHSP: SsqSsqHeadingSsqProperty) => sSHSP.ssqProperty)
            .map((p: Property) => p.id);
        const propertyList = this.getByPropertyList(propertyIds);

        const allProperties = new PropertyBuilder<ExtraPropertyKeys>({
            listOfProperties: propertyList,
            propertiesScore: propertyScores?.values ?? [],
            postElementAndCampaign: this.post,
        }).build();

        return allProperties;
    };

    public getByPropertyId = (options: {
        headingId: number;
        subheadingId?: number | undefined;
        propertyId: number;
    }) => {
        const { headingId, subheadingId, propertyId } = options;

        const headingData = this._getByHeadingId(headingId);
        const headingForProperties =
            subheadingId === undefined ? headingData : this._getBySubheadingId(subheadingId, headingData);

        if (headingForProperties === null) return null;

        const properties = this.handlePropertyDimension(headingForProperties);
        const property = properties?.find((property) => property.property.id === propertyId);
        if (property === undefined) return null;

        const propertiesScoresDimension = OlapScoresService.getDimension("properties", this.olapScores?.dimensions);
        const propertiesScoresValue = OlapScoresService.getDimensionValue(
            property.property.id,
            propertiesScoresDimension,
        );

        if ("content" in property) {
            const subpropertiesScoresDimension = OlapScoresService.getDimension(
                "subproperties",
                propertiesScoresValue?.dimensions,
            );

            property.content = property.content.map((value, position) => {
                if (Array.isArray(value)) {
                    return value.map((subproperty) => {
                        const subpropertiesScoresValue = OlapScoresService.getDimensionValue(
                            subproperty.property.id,
                            subpropertiesScoresDimension,
                        );
                        const multivaluesScoresDimension = OlapScoresService.getDimension(
                            "multivalues",
                            subpropertiesScoresValue?.dimensions,
                        );
                        const multivaluesScoresValue = multivaluesScoresDimension?.values[position];

                        return {
                            ...subproperty,
                            score: OlapScoresService.normalizeNumericalScore(multivaluesScoresValue?.score.value),
                        };
                    });
                }

                return value;
            }) as PropertyValue[] | PropertyValue[][];
        }

        return { property };
    };

    /**
     * Internals below
     */
    private _getByHeadingId = (headingId: number): SsqSsqHeading | null => {
        const ssqSsqHeading = this.ssqVersion.parentSsqSsqHeadings.find((h) => h.id === headingId);

        return ssqSsqHeading ?? null;
    };

    private _getBySubheadingId = (subheadingId: number, ssqSsqHeading: SsqSsqHeading | null): SsqSsqHeading | null => {
        if (ssqSsqHeading === null) return null;

        const subheading = ssqSsqHeading?.children.find((subheading: SsqSsqHeading) => subheading.id === subheadingId);

        return subheading ?? null;
    };

    private getByPropertyList = (ids: number[]): PropertyWithIndexes[] => {
        const acc: PropertyWithIndexes[] = [];
        const propertyList = ids.reduce((prevAcc, currentId) => {
            const existingProperty = this._allPropertyByIds?.[currentId];
            if (existingProperty === undefined) return prevAcc;
            const propertyWithIndexes: PropertyWithIndexes = {
                ...existingProperty,
                extraIndexes: {},
            };

            prevAcc.push(propertyWithIndexes);
            return prevAcc;
        }, acc);

        return propertyList;
    };

    private propertyListToMapping = (properties: SsqProperty[], existingAcc: PropertyMapping): PropertyMapping => {
        const _propertyAcc: PropertyMapping = existingAcc;
        const propertyAcc = properties.reduce((acc, property) => {
            acc["byProperty"][property.id] = property;
            return acc;
        }, _propertyAcc);
        return propertyAcc;
    };

    private preparePropertyIdMapping = (ssqVersion: SsqVersion) => {
        const acc: PropertyMapping = { byHeadingId: {}, byProperty: {} };

        return ssqVersion.parentSsqSsqHeadings.reduce((accHeading, ssqSsqHeading: SsqSsqHeading) => {
            const properties = this.propertyListToMapping(
                ssqSsqHeading.ssqSsqHeadingSsqProperties.map(
                    (headingProperty: SsqSsqHeadingSsqProperty) => headingProperty.ssqProperty,
                ),
                accHeading,
            );

            return ssqSsqHeading.children.reduce((_accHeading2: PropertyMapping, subSsqSsqHeading: SsqSsqHeading) => {
                return this.propertyListToMapping(
                    subSsqSsqHeading.ssqSsqHeadingSsqProperties.map(
                        (headingProperty: SsqSsqHeadingSsqProperty) => headingProperty.ssqProperty,
                    ),
                    accHeading,
                );
            }, properties);
        }, acc);
    };
}
